<template>
    <OddsDisplayBlock
        v-bind:groups="groups"
        v-bind:loading="!loaded"
        v-bind:initialIsExpanded="() => true"
        enableVirtualScroll
    />

    <Teleport v-if="info" to=".leagueInfoIcon">
        <!-- teleport to src/views/euro/betting/League.vue -->
        <SvgIcon
            v-tooltip="info"
            name="icon-system-cycle-info"
            right
        />
    </Teleport>
</template>

<script lang="ts" setup>
    import groupBy from 'lodash/groupBy';
    import orderBy from 'lodash/orderBy';
    import type { Ref } from 'vue';
    import { computed, watchEffect } from 'vue';
    import SvgIcon from '@/components/common/SvgIcon.vue';
    import OddsDisplayBlock from '@/components/euroDisplay/oddsDisplay/OddsDisplayBlock.vue';
    import { useEuroRoute } from '@/composable/useEuroRoute';
    import { formatDateWithLocale } from '@/core/lib/date';
    import { getTranslated } from '@/core/lib/translationHelper';
    import { useEvents } from '@/core/oddsApi/composable/useEvents.euro';
    import { EnumEventDateFilterType, EnumOddsCategoryType, EnumPresetFilterType } from '@/core/oddsApi/oddsApiType';
    import type { IEventEuro } from '@/interface/IEvent';

    const { sportType, leagueId, leagueName, regionName, updateNameParams } = useEuroRoute();

    const leagueIds = computed(() => [leagueId.value]);

    const { events, loaded }: { events: Ref<IEventEuro[]>, loaded: Ref<boolean> } = useEvents({
        sportType,
        presetFilter: { presetFilter: EnumPresetFilterType.All, date: EnumEventDateFilterType.All },
        oddsCategory: EnumOddsCategoryType.All,
        leagueIds,
    });

    const groups = computed(() => {
        const titleFormatter = (showTime: Date) => formatDateWithLocale(showTime, 'E MM/dd');
        const groups = Object.values(groupBy(events.value, e => titleFormatter(e.showTime)));
        return groups.map((group) => {
            const events = orderBy(group, [e => e.isLive, e => e.eventCode], ['desc', 'asc']);
            return {
                id: events[0].id,
                events,
                title: titleFormatter(events[0].showTime),
            };
        });
    });

    watchEffect(() => {
        if (!events.value[0]) return;

        const translatedRegionName = getTranslated(events.value[0].league.region.name);
        const translatedLeagueName = getTranslated(events.value[0].league.name);

        if (translatedRegionName === regionName.value
            && translatedLeagueName === leagueName.value) return;

        updateNameParams({
            regionName: translatedRegionName,
            leagueName: translatedLeagueName,
        });
    });

    const info = computed(() => getTranslated(events.value[0]?.league.info));
</script>
